import React, { useState } from "react";
import {
  FaBars,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaPinterest,
  FaTiktok,
  FaTwitter,
  FaVimeo,
  FaYoutube,
} from "react-icons/fa";
import { ILinkFunc } from "../../lib/util";
import { ITextWidgetSearchProps, TextWidgetSearch } from "../Search";
import { EHeaderLayout } from "./Header";

import SubNav, { ISubNavProps } from "./SubNav";
import { ESocialChannel, ISocialLink } from "../../lib";

export interface INavItem {
  label: string;
  url?: string;
  subNav?: ISubNavProps;
}

export interface INavProps {
  className?: string | null;
  searchPath?: string;
  searchText?: string;
  signInPath?: string;
  navItems: INavItem[];
  linkFunc?: ILinkFunc;
  onSubmit?: (filters: {}) => void;
  layout?: EHeaderLayout;
  socialMediaLinks?: ISocialLink[];
  navItemClassNames?: string;
  textSearchWidgetProps?: Partial<ITextWidgetSearchProps>;
}

export const Nav: React.FC<INavProps> = (props) => {
  const [subOpen, setSubOpen] = useState(-1);
  const toggleSub = (index: number) => {
    if (subOpen === index) {
      setSubOpen(-1);
    } else {
      setSubOpen(index);
    }
  };

  const [navOpen, setNavOpen] = useState(false);
  const toggleNav = () => {
    setNavOpen(!navOpen);
    //if nav layout is side, close the subnav box when clicking the button
    if (props.layout == EHeaderLayout.side) {
      setSubOpen(-1)
    }
  };

  const handleEscape = (escape: boolean = false) => {
    //if nav layout is "side"
    if (props.layout == EHeaderLayout.side) {
      //Prevent the subnav box to close if not by hitting enter
      if (subOpen > -1 && escape) {
        setSubOpen(-1)
      }
      //if the subnav is closed, we can close de nav item
      else if (subOpen == -1) {
        setNavOpen(false)
      }
    }
    else {
      if (subOpen > -1) {
        setSubOpen(-1)
      } else {
        setNavOpen(false)
      }
    }
  }

  const linkf =
    props.linkFunc ||
    ((props) => (
      <a
        href={props.href}
        key={props.key}
        className={props.className}
        data-text={props.key}
      >
        {props.children}
      </a>
    ));

  const activeGlitch =
    "glitch-item md:inline-flex md:flex-col md:items-center md:after:content-[attr(aria-label)] md:after:font-extrabold md:after:underline md:after:underline-offset-2 md:after:invisible md:after:truncate md:after:h-0";

  const layoutClasses = {
    nav: "",
    button: "",
    list: "",
    listItem: "",
    link: "",
  };

  switch (props.layout) {
    case EHeaderLayout.top:
    case EHeaderLayout.top_spread:
      layoutClasses.nav = "order-last md:order-[unset]";
      layoutClasses.button = "text-3xl md:hidden";
      layoutClasses.list = `${navOpen ? "" : "hidden md:flex"
        } flex gap-5 py-5 px-5 md:px-0 md:py-0 flex-col absolute top-full right-0 left-0 bg-white border-t-2 border-t-body-text md:border-none md:bg-transparent md:static md:flex-row`;
      layoutClasses.listItem = "relative";
      layoutClasses.link = "text-body-text";
      break;
    case EHeaderLayout.side:
      layoutClasses.nav = "";
      layoutClasses.button =
        "w-[70px] md:w-full text-3xl md:text-5xl min-h-[70px] md:min-h-[100px] flex justify-center items-center md:border-b";
      layoutClasses.list = `${navOpen ? "" : "hidden"
        } z-10 flex flex-col gap-2 md:gap-0 md:justify-around fixed top-[70px] md:top-0 left-0 md:left-[100px] bottom-0 right-0 md:right-1/2 lg:right-[40%] border-t md:border-t-0 md:border-l p-6 md:py-20 md:px-10 inverted overflow-y-scroll md:overflow-auto ${subOpen !== -1 ? "subOpen" : ""}`;
      layoutClasses.listItem = "relative md:static";
      layoutClasses.link =
        "inverted md:bg-transparent text-3xl lg:text-5xl hover:text-inherit";
      break;
  }

  return (
    <nav
      role="navigation"
      aria-label="Main menu"
      className={`${props.className} ${layoutClasses.nav}`}
      onBlur={
        (e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            handleEscape();
          }
        }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          handleEscape(true);
        }
      }}
    >
      <button
        aria-expanded="true"
        aria-controls="Main menu"
        className={layoutClasses.button}
        onClick={toggleNav}
      >
        {navOpen ? <FaTimes /> : <FaBars />}
      </button>
      <ul className={`list-none ${layoutClasses.list}`}>
        {props.navItems.map((item: INavItem, index: number) => (
          <li
            className={`${layoutClasses.listItem} whitespace-nowrap ${props.navItemClassNames}`}
            key={index}
          >
            {item.url &&
              linkf({
                href: item.url,
                children: item.label,
                className: `${activeGlitch} ${layoutClasses.link} hover:font-extrabold hover:underline hover:underline-offset-2`,
                key: item.label,
              })}
            {item.subNav && (
              <>
                <button
                  data-text={item.label}
                  className={`${activeGlitch} ${layoutClasses.link
                    } flex items-center w-full md:w-auto justify-between hover:font-extrabold hover:underline hover:underline-offset-2 ${subOpen === index
                      ? "font-extrabold underline underline-offset-2"
                      : ""
                    }`}
                  onClick={() => {
                    toggleSub(index);
                  }}
                >
                  {item.label}
                  {(props.layout === EHeaderLayout.top || props.layout === EHeaderLayout.top_spread) &&
                    (subOpen ? (
                      <FaChevronDown className="float-right md:hidden" />
                    ) : (
                      <FaChevronUp className="float-right md:hidden" />
                    ))}
                </button>
                <SubNav
                  linkFunc={linkf}
                  subNavGroups={item.subNav.subNavGroups}
                  className={subOpen === index ? "visible" : "invisible hidden"}
                  layout={props.layout}
                  setNavOpen={setNavOpen}
                  setSubOpen={setSubOpen}
                />
              </>
            )}
          </li>
        ))}
        {props.socialMediaLinks && props.socialMediaLinks.length > 0 && (
          <li className="flex gap-5 text-inherit">
            {props.socialMediaLinks.map((item: ISocialLink, index: number) => (
              <a
                key={index}
                className="text-inherit"
                href={`${item.url}`}
                title={item.text}
              >
                {(() => {
                  switch (item.channel) {
                    case ESocialChannel.Facebook:
                      return <FaFacebookF />;
                    case ESocialChannel.Twitter:
                      return <FaTwitter />;
                    case ESocialChannel.Instagram:
                      return <FaInstagram />;
                    case ESocialChannel.YouTube:
                      return <FaYoutube />;
                    case ESocialChannel.LinkedIn:
                      return <FaLinkedinIn />;
                    case ESocialChannel.Vimeo:
                      return <FaVimeo />;
                    case ESocialChannel.Pinterest:
                      return <FaPinterest />;
                    case ESocialChannel.TikTok:
                      return <FaTiktok />;
                    default:
                      return null;
                  }
                })()}
              </a>
            ))}
          </li>
        )}
        {props.searchPath && (
          <li>
            <TextWidgetSearch
              placeholder={props.searchText || "Keywords"}
              onSubmit={props.onSubmit || (() => { })}
              expand="left"
              className="-mt-2"
              openClassName="w-full"
              {...props.textSearchWidgetProps}
            />
          </li>
        )}

        {props.signInPath && (
          <li className="border-l border-l-body-text pl-3">
            {linkf({
              href: props.signInPath,
              children: <button>Sign In</button>,
            })}
          </li>
        )}
      </ul>
    </nav>
  );
};



export default Nav;
